import type { Edge, Vertex } from './types'

const vertexKey = (v: Vertex): string => `${v.id}|${v.lineID}`
const edgeKey = (e: Edge): string => `${vertexKey(e.from)}>${vertexKey(e.to)}|${e.realID}`

/**
 * Graph is a directed adjacency map plus an opt-in deactivation set.
 *
 * Every vertex (one per id + line) and every edge carries a dense index, in
 * insertion order, so the search can run on integer arrays instead of
 * building and hashing a string per relaxation: `vertexIndex`,
 * `outgoingEdges`, `isActive`, `edgeAt`. Edges that share endpoints and
 * RealID deactivate together, as one key. iOS twin: `Graph.swift`.
 *
 * Concurrency contract: in JS the single-threaded event loop guarantees
 * non-overlapping mutator/reader execution. The hazard appears with async
 * iteration — if `findRoutes` awaits between `deactivate` and `restore`,
 * a concurrent `findRoutes` could read mid-deactivated state. The host
 * store must therefore serialize `findRoutes` calls (single in-flight).
 */
export class Graph {
  private readonly vertexIndexByKey = new Map<string, number>()
  private readonly vertices: Vertex[] = []
  private readonly outgoing: number[][] = []
  /** Vertices in the order an edge first left them — the `allEdges` order. */
  private readonly sourceOrder: number[] = []
  private readonly edges: Edge[] = []
  private readonly edgeTo: number[] = []
  private readonly edgeGroup: number[] = []
  private readonly groupByKey = new Map<string, number>()
  private readonly groupDeactivated: boolean[] = []
  private deactivatedGroups = 0
  private readonly derivedValues = new Map<string, unknown>()

  /** Number of distinct (id, line) vertices — the range of every vertex index. */
  get vertexCount(): number {
    return this.vertices.length
  }

  /** Number of edges — the range of every edge index. */
  get edgeCount(): number {
    return this.edges.length
  }

  addEdge(e: Edge): void {
    const index = this.edges.length
    const from = this.intern(e.from)
    const to = this.intern(e.to)
    const key = edgeKey(e)
    let group = this.groupByKey.get(key)
    if (group === undefined) {
      group = this.groupDeactivated.length
      this.groupByKey.set(key, group)
      this.groupDeactivated.push(false)
    }
    if (this.outgoing[from].length === 0) this.sourceOrder.push(from)
    this.edges.push(e)
    this.edgeTo.push(to)
    this.edgeGroup.push(group)
    this.outgoing[from].push(index)
    this.derivedValues.clear()
  }

  edgesFrom(v: Vertex): readonly Edge[] {
    const index = this.vertexIndexByKey.get(vertexKey(v))
    if (index === undefined) return []
    const out: Edge[] = []
    for (const edge of this.outgoing[index]) if (this.isActive(edge)) out.push(this.edges[edge])
    return out
  }

  deactivate(e: Edge): void {
    const group = this.groupByKey.get(edgeKey(e))
    if (group !== undefined) this.setGroup(group, true)
  }
  restore(e: Edge): void {
    const group = this.groupByKey.get(edgeKey(e))
    if (group !== undefined) this.setGroup(group, false)
  }
  restoreAll(): void {
    this.groupDeactivated.fill(false)
    this.deactivatedGroups = 0
  }

  allVertexIDs(): string[] {
    return [...new Set(this.vertices.map((v) => v.id))]
  }

  /** Every edge, grouped by the vertex it leaves, vertices in first-use order. */
  allEdges(): Edge[] {
    const out: Edge[] = []
    for (const from of this.sourceOrder)
      for (const edge of this.outgoing[from]) out.push(this.edges[edge])
    return out
  }

  // Indexed access

  /** The index of the vertex with `v`'s id and line, or `undefined` when no edge touches it. */
  vertexIndex(v: Vertex): number | undefined {
    return this.vertexIndexByKey.get(vertexKey(v))
  }

  /** The vertex at `index` — the first vertex added with its id and line. */
  vertexAt(index: number): Vertex {
    return this.vertices[index]
  }

  /** Indices of every edge leaving vertex `index`, active or not, in insertion order. */
  outgoingEdges(index: number): readonly number[] {
    return this.outgoing[index]
  }

  edgeAt(index: number): Edge {
    return this.edges[index]
  }

  /** The index of the vertex edge `index` leads to. */
  targetIndex(edge: number): number {
    return this.edgeTo[edge]
  }

  isActive(edge: number): boolean {
    return this.deactivatedGroups === 0 || !this.groupDeactivated[this.edgeGroup[edge]]
  }

  /** Hides edge `index` (and every edge sharing its key) until restored. */
  deactivateAt(edge: number): void {
    this.setGroup(this.edgeGroup[edge], true)
  }

  restoreAt(edge: number): void {
    this.setGroup(this.edgeGroup[edge], false)
  }

  /**
   * A value computed from this graph once and kept until the next `addEdge`
   * — the copies and lookup tables a search derives from the graph, built
   * once per graph instead of once per search.
   */
  derived<T>(key: string, make: (graph: Graph) => T): T {
    if (this.derivedValues.has(key)) return this.derivedValues.get(key) as T
    const value = make(this)
    this.derivedValues.set(key, value)
    return value
  }

  private intern(v: Vertex): number {
    const key = vertexKey(v)
    const existing = this.vertexIndexByKey.get(key)
    if (existing !== undefined) return existing
    const index = this.vertices.length
    this.vertexIndexByKey.set(key, index)
    this.vertices.push(v)
    this.outgoing.push([])
    return index
  }

  private setGroup(group: number, deactivated: boolean): void {
    if (this.groupDeactivated[group] === deactivated) return
    this.groupDeactivated[group] = deactivated
    this.deactivatedGroups += deactivated ? 1 : -1
  }
}
